/**
 * Wall-motion extraction from the echo DICOM-SR (real data confirmed in PACS:
 * GE "Adult Echocardiography Procedure Report" carries per-segment
 * `Wall Segment` → `Cardiac Wall Motion` codes on the AHA 17-segment model).
 *
 * No strain in these SRs — this reads the wall-motion score per LV segment.
 */
import { getPacsAETitle, getDicomWebProxyURL, getAppAuthToken } from '../../constant';

// ── DICOM-JSON helpers ──────────────────────────────────────────────────────
const tag = (n: any, t: string) => n?.[t]?.Value || [];
const valueType = (n: any) => tag(n, '0040A040')[0] || '';
const conceptMeaning = (n: any) => {
  const cn = tag(n, '0040A043')[0];
  return cn ? (cn['00080104']?.Value?.[0] || '') : '';
};
const codeValueMeaning = (n: any) => {
  const cv = tag(n, '0040A168')[0]; // Concept Code Sequence (the CODE value)
  return cv ? (cv['00080104']?.Value?.[0] || '') : '';
};
const childItems = (n: any) => tag(n, '0040A730'); // Content Sequence
const numericValue = (n: any) => {
  const mv = tag(n, '0040A300')[0];
  return mv ? mv['0040A30A']?.Value?.[0] : undefined;
};

// ── motion class → 5-point score / colour ───────────────────────────────────
export interface MotionInfo { score: number; label: string; color: string; }
export const MOTION_LEGEND: MotionInfo[] = [
  { score: 1, label: 'Normal', color: '#4ade80' },
  { score: 2, label: 'Hypokinetic', color: '#f7a35a' },
  { score: 3, label: 'Akinetic', color: '#fb7185' },
  { score: 4, label: 'Dyskinetic', color: '#a78bfa' },
  { score: 5, label: 'Aneurysmal', color: '#c084fc' },
];
const NO_DATA: MotionInfo = { score: 0, label: 'Not scored', color: '#243652' };

function classifyMotion(raw: string): MotionInfo {
  const v = (raw || '').toLowerCase();
  if (/aneurysm/.test(v)) return MOTION_LEGEND[4];
  if (/dyskin/.test(v)) return MOTION_LEGEND[3];
  if (/akin/.test(v)) return MOTION_LEGEND[2];
  if (/hypokin/.test(v)) return MOTION_LEGEND[1];
  if (/normal/.test(v)) return MOTION_LEGEND[0];
  return NO_DATA;
}

/** Normalise an SR segment name → canonical id, e.g.
 *  "left ventricle basal anteroseptal segment" → "basal anteroseptal". */
export function normalizeSegment(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .replace('left ventricle', '')
    .replace('segment', '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface WallMotionResult {
  segments: Record<string, MotionInfo>; // canonical id → motion
  wmsi: number | null; // LV Wall Motion Score Index (mean), if present
  scoredCount: number;
}

/** Walk a DICOM-JSON SR instance and pull every Wall Segment → Cardiac Wall Motion. */
export function extractWallMotion(srInstance: any): WallMotionResult {
  const segments: Record<string, MotionInfo> = {};
  let wmsi: number | null = null;

  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    const concept = conceptMeaning(node);

    if (concept === 'LV Wall Motion Score Index') {
      const nv = Number(numericValue(node));
      if (!isNaN(nv)) wmsi = Math.round(nv * 100) / 100;
    }
    // (SR-reported WMSI takes precedence; a computed fallback is applied after the
    //  walk when the SR scores the segments but omits the index concept.)

    if (concept === 'Wall Segment') {
      const segId = normalizeSegment(codeValueMeaning(node));
      // motion is a child Cardiac Wall Motion CODE
      let motion = '';
      for (const c of childItems(node)) {
        if (conceptMeaning(c) === 'Cardiac Wall Motion') {
          motion = codeValueMeaning(c);
          break;
        }
      }
      if (segId) segments[segId] = classifyMotion(motion);
    }

    for (const c of childItems(node)) walk(c);
  };
  walk(srInstance);

  // WMSI computed fallback: when the SR omits the index concept but per-segment
  // motion scores exist, WMSI = mean segment score (ASE: Σ score / scored segments).
  // Only segments with a real 1–5 score count (exclude "Not scored" = 0).
  if (wmsi === null) {
    const scored = Object.values(segments).filter(s => s.score >= 1 && s.score <= 5);
    if (scored.length) {
      const sum = scored.reduce((acc, s) => acc + s.score, 0);
      wmsi = Math.round((sum / scored.length) * 100) / 100;
    }
  }

  return { segments, wmsi, scoredCount: Object.keys(segments).length };
}

// ── fetch the SR(s) for a study and extract wall motion ─────────────────────
function authHeaders() {
  // Via the backend proxy: app login token, clinic scope enforced server-side.
  const token = getAppAuthToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export async function fetchWallMotion(studyInstanceUID: string): Promise<WallMotionResult> {
  const base = getDicomWebProxyURL();
  const root = `${base}/rs/studies/${studyInstanceUID}`;
  const headers = authHeaders();

  // 1. SR series
  const sres = await fetch(`${root}/series?Modality=SR&includefield=0020000E`, { headers });
  if (!sres.ok) throw new Error(`SR series query failed (${sres.status})`);
  const seriesList = await sres.json();
  const seriesUIDs = (seriesList || [])
    .map((s: any) => s['0020000E']?.Value?.[0])
    .filter(Boolean);

  // 2. walk each SR instance until one yields wall-motion segments
  for (const se of seriesUIDs) {
    const ires = await fetch(`${root}/series/${se}/instances?includefield=00080018`, { headers });
    if (!ires.ok) continue;
    const instances = await ires.json();
    for (const inst of instances || []) {
      const sop = inst['00080018']?.Value?.[0];
      if (!sop) continue;
      const mres = await fetch(`${root}/series/${se}/instances/${sop}/metadata`, { headers });
      if (!mres.ok) continue;
      const meta = await mres.json();
      const node = Array.isArray(meta) ? meta[0] : meta;
      const result = extractWallMotion(node);
      if (result.scoredCount > 0) return result;
    }
  }
  return { segments: {}, wmsi: null, scoredCount: 0 };
}
